import { Entity, nodes, env } from "@dinahmoe/dmafjs-core";

class ScrollHandler extends Entity {
  namespace = "se26:scrollHandler";

  handlers = {
    scrollValue: this.handleScrollChange,
    rootWidth: this.handleRootSizeChange,
    rootHeight: this.handleRootSizeChange,
    connectHeight: this.handlePendingScroll,
    searchHeight: this.handlePendingScroll,
  };

  init(node) {
    super.init(node);
    node._currentRouteId = null;
    node._sortedRoutes = null;
    node._targetPositions = null;
    node._detectionPositions = null;
    node._layoutReady = false;
    node._pendingRouteId = null;
  }

  resolveRoutes(node) {
    const sectionsNode = nodes.get(node.sectionsUid);
    const rootHeight = nodes.get("root")['size.height'];
    node._targetPositions = {};
    node._detectionPositions = {};
    node._sortedRoutes = null;
    for (const [routeId, sectionProp] of Object.entries(node.routes)) {
      const sectionValue = sectionsNode[sectionProp];
      node._targetPositions[routeId] = sectionValue;
      node._detectionPositions[routeId] = sectionValue === 0 ? 0 : sectionValue - rootHeight / 1.5;
    }
  }

  handleRootSizeChange(node) {
    this.resolveRoutes(node);

    if (!node._layoutReady && node.rootHeight > 0) {
      node._layoutReady = true;
      this.dispatch("layout:ready");
    }

    if (node._currentRouteId) {
      this.attemptScroll(node, node._currentRouteId);
    }
  }

  setRoute(node, config, event) {
    const { routeId, source } = event.data || {};
    if (source === 'scroll' || source === 'overlay') return;
    if (!node._targetPositions) this.resolveRoutes(node);

    node._currentRouteId = routeId;
    node._navigatingFromMenu = true;

    this.attemptScroll(node, routeId);
  }

  handlePendingScroll(node) {
    if (!node._pendingRouteId) return;
    this.attemptScroll(node, node._pendingRouteId);
  }

  attemptScroll(node, routeId) {
    const lookupId = (routeId === "case-story" || routeId === "project") ? "showcase" : routeId;
    let scrollPosition = node._targetPositions[lookupId];

    if (routeId === 'download' && scrollPosition === undefined) {
      scrollPosition = this.getDownloadScrollPosition(node);
    }

    if (scrollPosition === undefined) {
      node._pendingRouteId = routeId;
      return;
    }

    node._pendingRouteId = null;
    this.scrollTo(node, scrollPosition);
  }

  scrollTo(node, scrollPosition) {
    const target = nodes.get(node.targetUid);
    if (target && env.motion !== "reduced") {
      target["value.target"] = scrollPosition !== undefined ? scrollPosition : target["value.max"];
    } else {
      const container = nodes.get("container_content");
      if (container && scrollPosition !== undefined) {
        container['scroll.y'] = scrollPosition;
      }
    }
  }

  handleScrollChange(node) {
    if (!node._targetPositions || !node.navigationUid) return;
    if (node._currentRouteId === null) return;

    let routeId = this.getRouteForScrollPosition(node, node.scrollValue);

    if (node.connectHeight > 0 && node.searchHeight > 0) {
      const sectionsNode = nodes.get(node.sectionsUid);
      const rootHeight = nodes.get("root")['size.height'];
      const downloadDetection = env.motion === "reduced"
        ? sectionsNode.connect + node.connectHeight - rootHeight / 4
        : sectionsNode.connect + node.connectHeight + node.searchHeight - rootHeight / 4;
      if (node.scrollValue >= downloadDetection) {
        routeId = 'download';
      }
    }

    if (node._navigatingFromMenu && routeId === node._currentRouteId) {
      node._navigatingFromMenu = false;
    }

    if (node._navigatingFromMenu) return;
    if (!routeId || routeId === node._currentRouteId) return;

    node._currentRouteId = routeId;
    this.dispatch(`${node.navigationUid}:navigate:${routeId}`, 0, { source: "scroll" });
  }

  getRouteForScrollPosition(node, scrollValue) {
    if (!node._sortedRoutes) {
      node._sortedRoutes = Object.entries(node._detectionPositions)
        .map(([id, position]) => ({ id, position }))
        .sort((a, b) => a.position - b.position);
    }
    for (let i = node._sortedRoutes.length - 1; i >= 0; i--) {
      const route = node._sortedRoutes[i];
      if (scrollValue >= route.position) {
        return route.id;
      }
    }
    return node._sortedRoutes[0]?.id;
  }

  getDownloadScrollPosition(node) {
    const sectionsNode = nodes.get(node.sectionsUid);
    if (!sectionsNode) return undefined;
    if (!node.connectHeight || !node.searchHeight) return undefined;
    if (env.motion === "reduced") {
      return sectionsNode.connect + node.connectHeight + 300;
    }
    return sectionsNode.connect + node.connectHeight + node.searchHeight + 300;
  }
}

const scrollHandler = new ScrollHandler();
export { scrollHandler, ScrollHandler };
